/* Ranks come from XP only. Rank I values run 1-3-10 (Bronze 1,000 ... Grandmaster 1,000,000); inside a
   rank II is 1.5x and III is 2x the rank's I. Grandmaster keeps going forever: II 1.5M, III 2M, then
   +500,000 per level. Below Bronze I there is no ring. */

export type Tier = 'none' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'master' | 'gm'

export const TIERS: { id: Exclude<Tier, 'none'>; name: string; base: number }[] = [
  { id: 'bronze', name: 'Bronze', base: 1_000 },
  { id: 'silver', name: 'Silver', base: 3_000 },
  { id: 'gold', name: 'Gold', base: 10_000 },
  { id: 'platinum', name: 'Platinum', base: 30_000 },
  { id: 'diamond', name: 'Diamond', base: 100_000 },
  { id: 'master', name: 'Master', base: 300_000 },
  { id: 'gm', name: 'Grandmaster', base: 1_000_000 },
]

export const TIER_NAMES: Record<Tier, string> = {
  none: 'Unranked', bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum',
  diamond: 'Diamond', master: 'Master', gm: 'Grandmaster',
}

export interface Rank {
  tier: Tier
  level: number // 1-based inside the tier (Grandmaster can go past 3)
  index: number // position in the full ladder, 0 = unranked
  name: string // "Platinum II"
  floor: number // XP where this rank starts
  next: number | null // XP where the next rank starts
  nextName: string | null
}

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']
export function roman(k: number): string {
  if (k <= 10) return ROMAN[k - 1]
  const map: [number, string][] = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']]
  let out = ''
  for (const [v, s] of map) while (k >= v) { out += s; k -= v }
  return out
}

/* The full ladder of rank floors below Grandmaster, in order. */
const LADDER: { tier: Tier; level: number; floor: number }[] = []
for (const t of TIERS) {
  if (t.id === 'gm') continue
  LADDER.push({ tier: t.id, level: 1, floor: t.base }, { tier: t.id, level: 2, floor: t.base * 1.5 }, { tier: t.id, level: 3, floor: t.base * 2 })
}
export const GM_FLOOR = 1_000_000
export function gmFloor(level: number) {
  if (level <= 1) return GM_FLOOR
  if (level === 2) return 1_500_000
  return 2_000_000 + (level - 3) * 500_000
}

export function rankName(tier: Tier, level: number) {
  return tier === 'none' ? 'Unranked' : `${TIER_NAMES[tier]} ${roman(level)}`
}

export function rankFor(xpRaw: number): Rank {
  const xp = Math.max(0, Math.floor(xpRaw || 0))
  if (xp >= GM_FLOOR) {
    let level = 1
    while (xp >= gmFloor(level + 1)) level++
    const next = gmFloor(level + 1)
    return { tier: 'gm', level, index: LADDER.length + level, name: rankName('gm', level), floor: gmFloor(level), next, nextName: rankName('gm', level + 1) }
  }
  let i = -1
  for (let k = 0; k < LADDER.length; k++) if (xp >= LADDER[k].floor) i = k
  if (i < 0) {
    return { tier: 'none', level: 0, index: 0, name: 'Unranked', floor: 0, next: LADDER[0].floor, nextName: rankName(LADDER[0].tier, 1) }
  }
  const cur = LADDER[i]
  const nx = LADDER[i + 1]
  return {
    tier: cur.tier, level: cur.level, index: i + 1, name: rankName(cur.tier, cur.level), floor: cur.floor,
    next: nx ? nx.floor : GM_FLOOR, nextName: nx ? rankName(nx.tier, nx.level) : rankName('gm', 1),
  }
}

export const tierOf = (xp: number): Tier => rankFor(xp).tier

/* Coins for reaching a new tier's first level. */
export const TIER_REWARD: Record<Exclude<Tier, 'none'>, number> = {
  bronze: 2_500, silver: 5_000, gold: 10_000, platinum: 15_000, diamond: 25_000, master: 35_000, gm: 50_000,
}

/* Every rank boundary crossed between two XP totals, for the recap's rank-up moments. */
export function ranksCrossed(before: number, after: number): Rank[] {
  const out: Rank[] = []
  let r = rankFor(before)
  while (r.next != null && after >= r.next) {
    r = rankFor(r.next)
    out.push(r)
    if (out.length > 50) break
  }
  return out
}

/* Where you are between rings (tiers), for places that show rings but not rank numbers: the recap's bar. */
export function ringSpan(xp: number): { tier: Tier; name: string; floor: number; next: number | null; nextTier: Tier | null; nextName: string | null } {
  const tier = rankFor(xp).tier
  const i = TIERS.findIndex((t) => t.id === tier)
  const floor = tier === 'none' ? 0 : TIERS[i].base
  const nt = tier === 'none' ? TIERS[0] : TIERS[i + 1] || null
  return { tier, name: TIER_NAMES[tier], floor, next: nt ? nt.base : null, nextTier: nt ? nt.id : null, nextName: nt ? nt.name : null }
}
